import { useState, useCallback } from 'react';
import { apiClient } from '../index';
import { Subscriber, SubscriberUploadChunkResponse, SubscriberUpdateChunkResponse } from '../types';
import { getCsvChunkSize, getCsvExportChunkSize } from '../../config/settings';

interface UseSubscribersOptions {
  tags?: string;
  getAllIds?: boolean;
  domain?: string;
}

interface CreateSubscriberData {
  email: string;
  name?: string;
  tags?: string[];
}

export const useSubscribers = (options: UseSubscribersOptions = {}) => {
  const [data, setData] = useState<Subscriber[]>([]);
  const [subscriberIds, setSubscriberIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      
      if (options.tags) params.tags = options.tags;
      if (options.getAllIds) params.getAllIds = 'true';
      if (options.domain) params.domain = options.domain;

      const result = await apiClient.get<any>('/api/subscribers', params);
      
      if (options.getAllIds) {
        setSubscriberIds(result.subscriberIds || []);
      } else {
        setData(result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch subscribers');
    } finally {
      setLoading(false);
    }
  }, [options.tags, options.getAllIds, options.domain]);

  const createSubscriber = useCallback(async (subscriberData: CreateSubscriberData) => {
    const result = await apiClient.post<Subscriber>('/api/subscribers', subscriberData);
    await fetchSubscribers();
    return result;
  }, [fetchSubscribers]);

  const updateSubscriber = useCallback(async (id: number, subscriberData: CreateSubscriberData) => {
    const result = await apiClient.put<Subscriber>(`/api/subscribers/${id}`, subscriberData);
    await fetchSubscribers();
    return result;
  }, [fetchSubscribers]);

  const deleteSubscriber = useCallback(async (id: number) => {
    await apiClient.delete(`/api/subscribers/${id}`);
    await fetchSubscribers();
  }, [fetchSubscribers]);

  const uploadCsv = useCallback(async (file: File, skipDebounce?: boolean) => {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0];
    const dataRows = lines.slice(1);
    
    const chunkSize = await getCsvChunkSize();
    const chunks = [];
    for (let i = 0; i < dataRows.length; i += chunkSize) {
      chunks.push(dataRows.slice(i, i + chunkSize));
    }
    
    const uploadPromises = chunks.map((chunk, index) => {
      const csvContent = [headers, ...chunk].join('\n');
      const chunkFile = new File([csvContent], `chunk_${index}.csv`, { type: 'text/csv' });
      const formData = new FormData();
      formData.append('file', chunkFile);
      if (skipDebounce) formData.append('skipDebounce', 'true');
      return apiClient.upload('/api/subscribers/upload-csv', formData);
    });
    
    const results = await Promise.allSettled(uploadPromises);
    await fetchSubscribers();
    
    // Aggregate results from all successful chunks
    const successfulResults: SubscriberUploadChunkResponse[] = results
      .filter(r => r.status === 'fulfilled')
      .map(r => (r as PromiseFulfilledResult<SubscriberUploadChunkResponse>).value);
    const totalImported = successfulResults.reduce((sum, result) => sum + (result.imported || 0), 0);
    const totalTagsCreated = successfulResults.reduce((sum, result) => sum + (result.tagsCreated || 0), 0);
    const totalTagRelationships = successfulResults.reduce((sum, result) => sum + (result.tagRelationships || 0), 0);
    const totalSkipped = successfulResults.reduce((sum, result) => sum + (result.skipped || 0), 0);
    const allErrors = successfulResults.flatMap(result => result.errors || []);

    const duplicateEmails = Array.from(new Set(successfulResults.flatMap(r => r.duplicateEmails || [])));
    return {
      totalChunks: chunks.length,
      successful: results.filter(r => r.status === 'fulfilled').length,
      failed: results.filter(r => r.status === 'rejected').length,
      totalImported,
      totalTagsCreated,
      totalTagRelationships,
      totalSkipped,
      errors: allErrors,
      results,
      duplicateEmails
    };
  }, [fetchSubscribers]);

  const bulkOptOut = useCallback(async (emails: string[]) => {
    return apiClient.post('/api/subscribers/bulk-optout', { emails });
  }, []);

  const getTopDomains = useCallback(async (tags: string) => {
    return apiClient.get<string[]>(`/api/subscribers/top-domains?tags=${tags}`);
  }, []);

  const getSources = useCallback(async () => {
    return apiClient.get<string[]>('/api/sources');
  }, []);

  const getCountries = useCallback(async () => {
    return apiClient.get<string[]>('/api/countries');
  }, []);

  const assignTagsToSubscribers = useCallback(async (filters: any, tag: string) => {
    return apiClient.post('/api/tags/assign-to-subscribers', { filters, tag });
  }, []);

  const assignMultipleTagsToSubscribers = useCallback(async (filters: any, tags: string[]) => {
    return apiClient.post('/api/tags/assign-multiple-to-subscribers', { filters, tags });
  }, []);

  const removeTagsFromSubscribers = useCallback(async (filters: any, tag: string) => {
    return apiClient.post('/api/tags/remove-from-subscribers', { filters, tag });
  }, []);

  const removeMultipleTagsFromSubscribers = useCallback(async (filters: any, tags: string[]) => {
    return apiClient.post('/api/tags/remove-multiple-from-subscribers', { filters, tags });
  }, []);

  const downloadSampleCsv = useCallback(async () => {
    return apiClient.downloadCsv('/api/subscribers/sample-csv');
  }, []);

  const deleteFiltered = useCallback(async (filters: any) => {
    return apiClient.post('/api/subscribers/delete-filtered', { filters });
  }, []);

  const refreshTagCounts = useCallback(async () => {
    return apiClient.post('/api/tags/refresh-counts');
  }, []);

  const optoutFiltered = useCallback(async (filters: any) => {
    return apiClient.post('/api/subscribers/optout-filtered', { filters });
  }, []);

  const downloadCsv = useCallback(async (filters: any) => {
    const headers = ['email', 'name', 'phone', 'source', 'optIn', 'ipAddress', 'countryCode', 'tags', 'entryDate', 'lastOpened', 'topics'];
    const allSubscribers: any[] = [];
    let page = 1;
    const exportChunkSize = await getCsvExportChunkSize();
    
    while (true) {
      const pageParams: Record<string, string> = { 
        page: page.toString(), 
        limit: exportChunkSize.toString() 
      };
      
      if (filters.email) pageParams.email = filters.email;
      if (filters.source?.length > 0) pageParams.source = filters.source.join(',');
      if (filters.tags?.length > 0) pageParams.tags = filters.tags.join(',');
      if (filters.negativeTags?.length > 0) pageParams.negativeTags = filters.negativeTags.join(',');
      if (filters.countries?.length > 0) pageParams.countries = filters.countries.join(',');
      if (filters.optIn === 'true' || filters.optIn === true) pageParams.optIn = 'true';
      if (filters.optIn === 'false' || filters.optIn === false) pageParams.optIn = 'false';
      if (filters.startDate) pageParams.startDate = filters.startDate;
      if (filters.endDate) pageParams.endDate = filters.endDate;
      if (filters.lastOpenedStartDate) pageParams.lastOpenedStartDate = filters.lastOpenedStartDate;
      if (filters.lastOpenedEndDate) pageParams.lastOpenedEndDate = filters.lastOpenedEndDate;
      if (filters.topic_id) pageParams.topic_id = filters.topic_id.toString();
      if (filters.domain) pageParams.domain = filters.domain;
      if (filters.tags?.length > 0) pageParams.showTags = 'true';

      const pageResult = await apiClient.get('/api/subscribers', pageParams) as any;
      const subscribers = pageResult.subscribers || [];
      
      allSubscribers.push(...subscribers);
      
      if (subscribers.length < exportChunkSize) {
        break;
      }
      
      page++;
    }
    
    let csvContent = headers.join(',') + '\n';
    allSubscribers.forEach(sub => {
      const row = headers.map(header => {
        const value = sub[header] || '';
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      });
      csvContent += row.join(',') + '\n';
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().slice(0, 10);
    a.download = `subscribers-${timestamp}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, []);

  const getSubscriberCount = useCallback(async (tags: string[], domain?: string) => {
    const params: Record<string, string> = {};
    if (tags.length > 0) params.tags = tags.join(',');
    if (domain) params.domain = domain;
    
    const result = await apiClient.get('/api/subscribers/count', params) as any;
    return result.totalSubscribers;
  }, []);

  const getFilteredSubscribers = useCallback(async (filters: any, page: number = 1) => {
    const params: Record<string, string> = { page: page.toString() };
    
    if (filters.email) params.email = filters.email;
    if (filters.source?.length > 0) params.source = filters.source.join(',');
    if (filters.tags?.length > 0) params.tags = filters.tags.join(',');
    if (filters.negativeTags?.length > 0) params.negativeTags = filters.negativeTags.join(',');
    if (filters.countries?.length > 0) params.countries = filters.countries.join(',');
    if (filters.optIn === 'true' || filters.optIn === true) params.optIn = 'true';
    if (filters.optIn === 'false' || filters.optIn === false) params.optIn = 'false';
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.lastOpenedStartDate) params.lastOpenedStartDate = filters.lastOpenedStartDate;
    if (filters.lastOpenedEndDate) params.lastOpenedEndDate = filters.lastOpenedEndDate;
    if (filters.tags?.length > 0) params.showTags = 'true';
    if (filters.topic_id) params.topic_id = filters.topic_id.toString();
    
    return apiClient.get('/api/subscribers', params);
  }, []);

  const getSubscriberSummary = useCallback(async (filters: any) => {
    const params: Record<string, string> = {};
    
    if (filters.email) params.email = filters.email;
    if (filters.source?.length > 0) params.source = filters.source.join(',');
    if (filters.tags?.length > 0) params.tags = filters.tags.join(',');
    if (filters.negativeTags?.length > 0) params.negativeTags = filters.negativeTags.join(',');
    if (filters.countries?.length > 0) params.countries = filters.countries.join(',');
    if (filters.optIn === 'true' || filters.optIn === true) params.optIn = 'true';
    if (filters.optIn === 'false' || filters.optIn === false) params.optIn = 'false';
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.lastOpenedStartDate) params.lastOpenedStartDate = filters.lastOpenedStartDate;
    if (filters.lastOpenedEndDate) params.lastOpenedEndDate = filters.lastOpenedEndDate;
    
    return apiClient.get('/api/subscribers/summary', params);
  }, []);

  const recalculateSnapshots = useCallback(async (startDate: string) => {
    return apiClient.post('/api/subscribers/recalculate-snapshots', { startDate });
  }, []);

  const updateCsv = useCallback(async (file: File) => {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0];
    const dataRows = lines.slice(1);
    
    const chunkSize = await getCsvChunkSize();
    const chunks = [];
    for (let i = 0; i < dataRows.length; i += chunkSize) {
      chunks.push(dataRows.slice(i, i + chunkSize));
    }
    
    const updatePromises = chunks.map((chunk, index) => {
      const csvContent = [headers, ...chunk].join('\n');
      const chunkFile = new File([csvContent], `chunk_${index}.csv`, { type: 'text/csv' });
      const formData = new FormData();
      formData.append('file', chunkFile);
      return apiClient.upload('/api/subscribers/update-csv', formData);
    });
    
    const results = await Promise.allSettled(updatePromises);
    await fetchSubscribers();
    
    // Aggregate results from all successful chunks
    const successfulResults: SubscriberUpdateChunkResponse[] = results
      .filter(r => r.status === 'fulfilled')
      .map(r => (r as PromiseFulfilledResult<SubscriberUpdateChunkResponse>).value);
    const totalUpdated = successfulResults.reduce((sum, result) => sum + (result.updated || 0), 0);
    const totalTagsCreated = successfulResults.reduce((sum, result) => sum + (result.tagsCreated || 0), 0);
    const totalTagRelationships = successfulResults.reduce((sum, result) => sum + (result.tagRelationships || 0), 0);
    const totalSkipped = successfulResults.reduce((sum, result) => sum + (result.skipped || 0), 0);
    const allErrors = successfulResults.flatMap(result => result.errors || []);

    const notFoundEmails = Array.from(new Set(successfulResults.flatMap(r => r.notFoundEmails || [])));
    return {
      totalChunks: chunks.length,
      successful: results.filter(r => r.status === 'fulfilled').length,
      failed: results.filter(r => r.status === 'rejected').length,
      totalUpdated,
      totalTagsCreated,
      totalTagRelationships,
      totalSkipped,
      errors: allErrors,
      results,
      notFoundEmails
    };
  }, [fetchSubscribers]);

  return {
    data,
    subscriberIds,
    loading,
    error,
    refetch: fetchSubscribers,
    createSubscriber,
    updateSubscriber,
    deleteSubscriber,
    uploadCsv,
    bulkOptOut,
    getTopDomains,
    getSources,
    getCountries,
    assignTagsToSubscribers,
    removeTagsFromSubscribers,
    downloadSampleCsv,
    deleteFiltered,
    refreshTagCounts,
    optoutFiltered,
    downloadCsv,
    getSubscriberCount,
    getFilteredSubscribers,
    getSubscriberSummary,
    recalculateSnapshots,
    assignMultipleTagsToSubscribers,
    removeMultipleTagsFromSubscribers,
    updateCsv
  };
};